import {FormikProps, useFormik} from "formik";
import {FC, useMemo} from "react";
import {SearchSelectInput, TextArea, Button} from "components/core";
import {DistrictPicker} from "../../../containers";
import {PlusIcon, ArchiveBoxIcon, XMarkIcon} from "@heroicons/react/24/outline";
import * as Yup from "yup"
import lodash from "lodash";
import {Disclosure, DisclosureButton, DisclosurePanel} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import {classNames} from "../../../utils";

export * from "./schema";

interface AllocateMeterDeliveryMaterialsFormProps {
  form: FormikProps<{
    allocations: {
      district: any,
      materials:  {
        material: string,
        quantity: number,
      }[],
    }[],
    notes: string,
  }>,
  materials?: {
    material: {
      _id: string,
      name: string,
      code: string,
    },
    quantity: number,
  }[]
}

export const AllocateMeterDeliveryMaterialsForm: FC<AllocateMeterDeliveryMaterialsFormProps> = ({ form, materials }) => {

  const newDistrictForm = useFormik<any>({
    initialValues: {
      district: null,
    },
    validationSchema: Yup.object({
      district: Yup.object({ }).required("Required")
    }),
    onSubmit: async (values) => {

      // check for existing district;
      const existingDistrict = form.values.allocations.find(
        allocation => allocation?.district?._id === values.district._id
      )

      if(existingDistrict) return;

      form.setValues(prev => ({
        ...prev,
        allocations: [
          ...prev.allocations,
          {
            district: values.district,
            materials: [] as {
              material: any
              quantity: number,
            }[],
          }
        ]
      }))

      newDistrictForm.resetForm();
    }
  })

  const removeItem = (field: string, id: number) => {
    void form.setFieldValue(field, [
      ...lodash
        .get(form.values, field)
        ?.filter((val: string, _id: number) => _id !== id),
    ]);
  };


  const remainingQuantities = useMemo(() => {

    const quantities: { [key: string]: number } = { };

    if(!materials) return quantities;

    for(let i = 0; i < materials?.length; i++){
      let material = materials[i];
      quantities[material.material?._id] = material.quantity;
    }

    for(let i = 0; i < form.values.allocations?.length; i++){
      let allocation = form.values.allocations[i];

      for(let j = 0; j < allocation?.materials?.length; j++){
        let _material = allocation.materials[j];

        if(lodash.has(quantities, _material?.material)) {
          quantities[_material.material] -= (_material.quantity || 0)
        }
      }
    }

    return quantities;
  }, [materials, form.values])

  return (
    <div className='space-y-6 divide-y divide-gray-200'>
      <div>
        <span className='text-xs font-light'>Materials Allocation Information</span>
        <div className={"mt-4"}>
          {
            Boolean(form.values.allocations?.length) ? (
              <div className='space-y-2 divide-y divide-gray-200'>
                {
                  lodash.times(
                    form.values.allocations?.length,
                    (idx) => {

                      const id = `allocations.${idx}`;
                      const allocation = lodash.get(form.values, id);

                      return (
                        <div key={idx} className='border w-full border-gray-300 bg-gray-50 p-4 rounded'>
                          <div className='space-y-4'>
                            <div className='col-span-1'>
                              <div>
                                <Disclosure as="div" className='flex flex-col gap-3'>
                                  <DisclosureButton className='group flex flex-grow justify-between items-center'>
                                    <div className="flex flex-col items-start gap-2">
                                      <span className='text-xs font-light'>{allocation?.district?.code}</span>
                                      <span className='text-sm font-medium text-gray-900'>
                                        {allocation?.district?.name}
                                      </span>
                                    </div>
                                    <ChevronDownIcon
                                      className="w-5 h-5 shrink-0 text-gray-400 group-data-[open]:rotate-180"
                                    />
                                  </DisclosureButton>
                                  <DisclosurePanel
                                    transition
                                    className="grid grid-cols-4 gap-x-12 gap-y-4 transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0">

                                    <table className="min-w-full divide-y divide-gray-200 bg-white border col-span-4">
                                      <thead className="bg-gray-50">
                                      <tr>
                                        <th
                                          scope="col"
                                          className="pl-3 w-[40px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          No.
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          Material
                                        </th>
                                        <th
                                          scope="col"
                                          className="w-[100px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          Quantity
                                        </th>
                                        <th
                                          scope="col"
                                          className="w-[30px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                        </th>
                                      </tr>
                                      </thead>
                                      {/* Table Body */}
                                      <tbody className="divide-y divide-gray-200">
                                      {
                                        lodash.times(
                                          allocation.materials?.length + 1,
                                          (iddx) => {
                                            const idd = `allocations.${idx}.materials.${iddx}`;

                                            const value = lodash.get(form.values, `${idd}.quantity`, "")
                                            const remainingQuantity = remainingQuantities[lodash.get(form.values, `${idd}.material`, "")]

                                            const maxQuantity = (value || 0) + remainingQuantity;

                                            return (
                                              <tr key={iddx} className="divide-x divide-gray-200">
                                                <td className="p-0">
                                                  <input
                                                    key={iddx}
                                                    type={"text"}
                                                    id={`index`}
                                                    name={`index`}
                                                    value={`${iddx + 1}`}
                                                    placeholder={"eg. 1"}
                                                    disabled
                                                    className={classNames(
                                                      "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                                      "block w-[40px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                                    )}
                                                  />
                                                </td>
                                                <td className="p-0 flex-1">
                                                  <SearchSelectInput
                                                    {...form}
                                                    id={`${idd}.material`}
                                                    placeholder={'-- Select --'}
                                                    labelHidden={true}
                                                    errorsHidden={true}
                                                    options={
                                                      ((materials|| [])
                                                        ?.map((materials) => {

                                                          let id = materials?.material?._id;
                                                          let selectedMaterialIds = lodash.map(
                                                            lodash.get(form.values, `allocations.${idx}.materials`, []),
                                                            'material'
                                                          )
                                                          let disabled = selectedMaterialIds.includes(id)

                                                          return {
                                                            label: {
                                                              title: materials?.material?.name,
                                                            },
                                                            value: materials?.material?._id,
                                                            disabled,
                                                          }
                                                      })
                                                      )
                                                    }
                                                  />
                                                </td>
                                                <td className="">
                                                  <input
                                                    key={idx}
                                                    type={"number"}
                                                    id={`${idd}.quantity`}
                                                    name={`${idd}.quantity`}
                                                    value={value}
                                                    max={maxQuantity || ""}
                                                    min={1}
                                                    step={1}
                                                    onChange={(e) => {
                                                      const newVal = e.target.value;
                                                      if(!newVal) void form.setFieldValue(`${idd}.quantity`, undefined);
                                                      if(Number(newVal) < 1) void form.setFieldValue(`${idd}.quantity`, 0);

                                                      if(Number(newVal) > maxQuantity) {
                                                        void form.setFieldValue(`${idd}.quantity`, maxQuantity)
                                                      } else {
                                                        void form.setFieldValue(`${idd}.quantity`, Number(newVal))
                                                      }
                                                    }}
                                                    placeholder={"eg. 1"}
                                                    className={classNames(
                                                      "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                                                      "block w-[100px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                                    )}
                                                  />
                                                </td>
                                                <td className="">
                                                  <button
                                                    disabled={iddx === allocation.materials.length}
                                                    type={"button"}
                                                    onClick={() => removeItem(`allocations.${idx}.materials`, iddx)}
                                                    className={" grid place-items-center p-1 rounded-full hover:bg-gray-100 duration-150"}>
                                                    <XMarkIcon  className={"text-red-500 w-4 aspect-square"}  />
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          })
                                      }
                                      </tbody>
                                    </table>
                                  </DisclosurePanel>
                                </Disclosure>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }
                  )
                }
              </div>
            ) : (
              <div className='flex  mt-2 w-full items-center justify-center'>
                <div className='relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'>
                  <ArchiveBoxIcon
                    className='mx-auto h-10 w-10 text-gray-400'
                    stroke='currentColor'
                    strokeWidth={1}
                    aria-hidden='true'
                  />
                  <h3 className='mt-2 text-sm font-medium text-gray-900'>
                    No materials assigned
                  </h3>
                  <p className='mt-1 text-sm text-gray-500'>
                    Get started by adding a district below.
                  </p>
                </div>
              </div>
            )
          }

        </div>
        <div className='pt-6'>
          <form className={"flex justify-end"}
                onSubmit={newDistrictForm.handleSubmit}
          >
            <div>
              <DistrictPicker
                id={"district"}
                labelHidden
                {...newDistrictForm}
              />
            </div>
            <Button
              className={"flex gap-2"}
              type={'submit'}
              disabled={!newDistrictForm.isValid}
            >
              <PlusIcon className='w-5 h-5' />
              Add District
            </Button>
          </form>
        </div>

        <span className='text-xs font-light'>Submitted Materials Information</span>
        <div className={"my-4"}>

          <table className="min-w-full divide-y divide-gray-200 bg-white border">
            <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="pl-3 w-[40px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                No.
              </th>
              <th
                scope="col"
                className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Material
              </th>
              <th
                scope="col"
                className="w-[100px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Quantity
              </th>
              <th
                scope="col"
                className="w-[150px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Available Quantity
              </th>
            </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200">
            {
              lodash.times(
                materials?.length || 0,
                (idx) => {
                  const id = `${idx}`;

                  return (
                    <tr key={idx} className="divide-x divide-gray-200">
                      <td className="p-0">
                        <input
                          key={idx}
                          type={"text"}
                          id={`index`}
                          name={`index`}
                          value={`${idx + 1}`}
                          placeholder={"eg. 1"}
                          disabled
                          className={classNames(
                            "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                            "block w-[40px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                          )}
                        />
                      </td>
                      <td className="p-0 flex-1">
                        <input
                          key={idx}
                          value={lodash.get(materials, `${id}.material.name`, "")}
                          disabled
                          placeholder={"eg. "}
                          className={classNames(
                            "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                            "block w-flex sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                          )}
                        />
                      </td>
                      <td className="">
                        <input
                          key={idx}
                          type={"number"}
                          value={lodash.get(materials, `${id}.quantity`, "")}
                          disabled
                          placeholder={"eg. 1"}
                          className={classNames(
                            "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                            "block w-[100px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                          )}
                        />
                      </td>
                      <td className="">
                        <input
                          key={idx}
                          type={"number"}
                          value={
                            lodash.get(
                              remainingQuantities,
                              lodash.get(materials, `${id}.material._id`, 0)
                            )}
                          disabled
                          placeholder={"eg. 1"}
                          className={classNames(
                            "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                            "block w-[150px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border",
                            lodash.get(
                              remainingQuantities,
                              lodash.get(materials, `${id}.material._id`, 0)
                            ) > 0
                              ? "text-red-500 font-medium"
                              : "",
                          )}
                        />
                      </td>
                    </tr>
                  );
                })
            }
            </tbody>
          </table>

        </div>

        <div className='pt-6'>
          <div className={""}>
            <TextArea
              id="notes"
              label="Notes"
              placeholder="e.g. Additional notes..."
              rows={10}
              {...form}
            />
          </div>
        </div>
        {JSON.stringify(form.errors)}
      </div>
    </div>
  );
};

export default AllocateMeterDeliveryMaterialsForm;
